/**
 * AWS Setup Page - Scalable Cloud Deployment (~5 minutes)
 *
 * Step-by-step guide for connecting AWS account
 */

import axios from 'axios'
import { AlertCircle, Check, Cloud, ExternalLink, Loader } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8888'

interface AWSCredentials {
  access_key_id: string
  secret_access_key: string
  region: string
}

interface ResourceSelection {
  compute: boolean
  database: boolean
  storage: boolean
  ai: boolean
  cache: boolean
}

export default function AWSSetup() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [credentials, setCredentials] = useState<AWSCredentials>({
    access_key_id: '',
    secret_access_key: '',
    region: 'us-east-1'
  })
  const [resources, setResources] = useState<ResourceSelection>({
    compute: true,
    database: true,
    storage: true,
    ai: true,
    cache: true
  })
  const [validating, setValidating] = useState(false)
  const [deploying, setDeploying] = useState(false)
  const [deploymentProgress, setDeploymentProgress] = useState(0)
  const [deploymentMessage, setDeploymentMessage] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [ws, setWs] = useState<WebSocket | null>(null)

  const handleConnect = async () => {
    setValidating(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(
        `${API_URL}/api/aicloud/user-cloud/connect`,
        {
          provider: 'aws',
          credentials: credentials,
          tier: 'pro'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (response.data.success) {
        setStep(4)
      }
    } catch (err) {
      setError((err as any).response?.data?.detail as string | null || 'Failed to connect AWS account')
    } finally {
      setValidating(false)
    }
  }

  const handleDeploy = async () => {
    setDeploying(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(
        `${API_URL}/api/aicloud/user-cloud/deploy`,
        {
          provider: 'aws',
          resources: resources
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (response.data.success) {
        const deploymentId = String(response.data.deployment_id)
        const wsUrl = `wss://runai.clouplay.workers.dev/ws/deployment/${deploymentId}`
        const websocket = new WebSocket(wsUrl)

        websocket.onmessage = (event: MessageEvent) => {
          const data = JSON.parse(event.data as string) as {
            error?: string
            progress: number
            message: string
            status: string
          }

          if (data.error) {
            setError(data.error)
            setDeploying(false)
            return
          }

          setDeploymentProgress(data.progress)
          setDeploymentMessage(data.message)

          if (data.status === 'completed') {
            setDeploying(false)
            setStep(5)
          } else if (data.status === 'failed') {
            setError(data.message)
            setDeploying(false)
          }
        }

        websocket.onerror = () => {
          setError('WebSocket connection failed')
          setDeploying(false)
        }

        setWs(websocket)
      }
    } catch (err) {
      setError((err as any).response?.data?.detail as string | null || 'Failed to start deployment')
      setDeploying(false)
    }
  }

  useEffect(() => {
    return () => {
      if (ws) {
        ws.close()
      }
    }
  }, [ws])

  return (
    <div style={{ padding: '40px', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '40px' }}>
        <h1 style={{ fontSize: '36px', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
          <Cloud size={36} style={{ marginRight: '16px', color: '#ff9900' }} />
          AWS Setup
        </h1>
        <p style={{ fontSize: '18px', color: 'var(--text-secondary)' }}>
          Reliable deployment - Get your AI infrastructure running in ~5 minutes
        </p>
      </div>

      {/* Progress Steps */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '40px' }}>
        {[
          { num: 1, label: 'Account' },
          { num: 2, label: 'IAM Role' },
          { num: 3, label: 'Credentials' },
          { num: 4, label: 'Resources' },
          { num: 5, label: 'Deploy' }
        ].map((s) => (
          <div key={s.num} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: step >= s.num ? '#ff9900' : 'var(--card-bg)',
              border: step >= s.num ? 'none' : '2px solid var(--border-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 8px',
              color: step >= s.num ? 'white' : 'var(--text-secondary)'
            }}>
              {step > s.num ? <Check size={20} /> : s.num}
            </div>
            <div style={{ fontSize: '14px', color: step >= s.num ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '16px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          marginBottom: '32px',
          display: 'flex',
          alignItems: 'center',
          color: '#ef4444'
        }}>
          <AlertCircle size={20} style={{ marginRight: '12px' }} />
          {error}
        </div>
      )}

      {/* Step 1: Create Account */}
      {step === 1 && (
        <div style={{ padding: '32px', background: 'var(--card-bg)', borderRadius: '12px' }}>
          <h2 style={{ fontSize: '24px', marginBottom: '24px' }}>Step 1: Create AWS Account</h2>

          <div style={{ marginBottom: '24px' }}>
            <p style={{ marginBottom: '16px', lineHeight: '1.6' }}>
              If you don&apos;t have an AWS account yet, create one:
            </p>

            <a
              href="https://portal.aws.amazon.com/billing/signup"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '12px 24px',
                background: '#ff9900',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 'bold'
              }}
            >
              Create AWS Account
              <ExternalLink size={16} style={{ marginLeft: '8px' }} />
            </a>
          </div>

          <div style={{ padding: '16px', background: 'rgba(255, 153, 0, 0.1)', borderRadius: '8px', marginBottom: '24px' }}>
            <strong>Why AWS?</strong>
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li>Industry-leading reliability</li>
              <li>Global infrastructure (30+ regions)</li>
              <li>Bedrock: $0.003/1000 tokens</li>
              <li>DynamoDB: $0.25/GB/month</li>
              <li>S3: $0.023/GB/month</li>
            </ul>
          </div>

          <button
            onClick={() => setStep(2)}
            style={{
              padding: '12px 32px',
              background: '#ff9900',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            I have an AWS account
          </button>
        </div>
      )}

      {/* Step 2: Create IAM Role */}
      {step === 2 && (
        <div style={{ padding: '32px', background: 'var(--card-bg)', borderRadius: '12px' }}>
          <h2 style={{ fontSize: '24px', marginBottom: '24px' }}>Step 2: Create IAM Role</h2>

          <div style={{ marginBottom: '24px' }}>
            <p style={{ marginBottom: '16px', lineHeight: '1.6' }}>
              Create an IAM user with programmatic access:
            </p>

            <div style={{ padding: '16px', background: 'rgba(255, 153, 0, 0.05)', borderRadius: '8px', marginBottom: '16px' }}>
              <strong>AWS CLI Command:</strong>
              <pre style={{
                marginTop: '8px',
                padding: '12px',
                background: 'var(--body-bg)',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '14px'
              }}>
                {`aws iam create-user --user-name aicloud

aws iam attach-user-policy --user-name aicloud \\
  --policy-arn arn:aws:iam::aws:policy/PowerUserAccess

aws iam create-access-key --user-name aicloud`}
              </pre>
            </div>

            <div style={{ padding: '16px', background: 'rgba(255, 153, 0, 0.05)', borderRadius: '8px', marginBottom: '16px' }}>
              <strong>Required Information:</strong>
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                <li>Access Key ID</li>
                <li>Secret Access Key</li>
                <li>Region (e.g., us-east-1)</li>
              </ul>
            </div>

            <a
              href="https://console.aws.amazon.com/iam/home#/users"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '12px 24px',
                background: '#ff9900',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 'bold'
              }}
            >
              Open AWS Console
              <ExternalLink size={16} style={{ marginLeft: '8px' }} />
            </a>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <button
              onClick={() => setStep(1)}
              style={{
                padding: '12px 32px',
                background: 'transparent',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Back
            </button>
            <button
              onClick={() => setStep(3)}
              style={{
                padding: '12px 32px',
                background: '#ff9900',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              I have my IAM credentials
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Enter Credentials */}
      {step === 3 && (
        <div style={{ padding: '32px', background: 'var(--card-bg)', borderRadius: '12px' }}>
          <h2 style={{ fontSize: '24px', marginBottom: '24px' }}>Step 3: Enter Credentials</h2>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Access Key ID
            </label>
            <input
              type="text"
              value={credentials.access_key_id}
              onChange={(e) => setCredentials({ ...credentials, access_key_id: e.target.value })}
              placeholder="Enter your AWS access key ID"
              style={{
                width: '100%',
                padding: '12px',
                background: 'var(--body-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '16px',
                color: 'var(--text-primary)'
              }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Secret Access Key
            </label>
            <input
              type="password"
              value={credentials.secret_access_key}
              onChange={(e) => setCredentials({ ...credentials, secret_access_key: e.target.value })}
              placeholder="Enter your AWS secret access key"
              style={{
                width: '100%',
                padding: '12px',
                background: 'var(--body-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '16px',
                color: 'var(--text-primary)'
              }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Region
            </label>
            <select
              value={credentials.region}
              onChange={(e) => setCredentials({ ...credentials, region: e.target.value })}
              style={{
                width: '100%',
                padding: '12px',
                background: 'var(--body-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '16px',
                color: 'var(--text-primary)'
              }}
            >
              <option value="us-east-1">US East (N. Virginia)</option>
              <option value="us-west-2">US West (Oregon)</option>
              <option value="eu-west-1">EU (Ireland)</option>
              <option value="eu-central-1">EU (Frankfurt)</option>
              <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
              <option value="ap-northeast-1">Asia Pacific (Tokyo)</option>
            </select>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '8px' }}>
              Choose the region closest to your users
            </p>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <button
              onClick={() => setStep(2)}
              style={{
                padding: '12px 32px',
                background: 'transparent',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Back
            </button>
            <button
              onClick={() => { void handleConnect() }}
              disabled={!credentials.access_key_id || !credentials.secret_access_key || validating}
              style={{
                padding: '12px 32px',
                background: credentials.access_key_id && credentials.secret_access_key && !validating ? '#ff9900' : '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: credentials.access_key_id && credentials.secret_access_key && !validating ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {validating && <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />}
              {validating ? 'Validating...' : 'Connect Account'}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Select Resources */}
      {step === 4 && (
        <div style={{ padding: '32px', background: 'var(--card-bg)', borderRadius: '12px' }}>
          <h2 style={{ fontSize: '24px', marginBottom: '24px' }}>Step 4: Select Resources</h2>

          <p style={{ marginBottom: '24px', color: 'var(--text-secondary)' }}>
            Choose which resources to deploy to your AWS account:
          </p>

          {[
            { key: 'compute', label: 'Lambda (Compute)', desc: 'Serverless functions', price: '$0.20/M requests' },
            { key: 'database', label: 'DynamoDB (Database)', desc: 'NoSQL database', price: '$0.25/GB/month' },
            { key: 'storage', label: 'S3 (Storage)', desc: 'Object storage', price: '$0.023/GB/month' },
            { key: 'ai', label: 'Bedrock (AI)', desc: 'LLM inference', price: '$0.003/1000 tokens' },
            { key: 'cache', label: 'ElastiCache', desc: 'Redis cache', price: 'From $0.017/hour' }
          ].map((resource) => (
            <div
              key={resource.key}
              onClick={() => setResources({ ...resources, [resource.key]: !resources[resource.key as keyof ResourceSelection] })}
              style={{
                padding: '16px',
                background: resources[resource.key as keyof ResourceSelection] ? 'rgba(255, 153, 0, 0.1)' : 'var(--body-bg)',
                border: resources[resource.key as keyof ResourceSelection] ? '2px solid #ff9900' : '1px solid var(--border-color)',
                borderRadius: '8px',
                marginBottom: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{resource.label}</div>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{resource.desc}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{resource.price}</div>
              </div>
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '4px',
                background: resources[resource.key as keyof ResourceSelection] ? '#ff9900' : 'transparent',
                border: resources[resource.key as keyof ResourceSelection] ? 'none' : '2px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}>
                {resources[resource.key as keyof ResourceSelection] && <Check size={16} />}
              </div>
            </div>
          ))}

          <button
            onClick={() => { void handleDeploy() }}
            disabled={deploying}
            style={{
              width: '100%',
              padding: '16px',
              background: '#ff9900',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: deploying ? 'not-allowed' : 'pointer',
              opacity: deploying ? 0.6 : 1,
              marginTop: '24px'
            }}
          >
            {deploying ? 'Deploying...' : 'Deploy to AWS'}
          </button>
        </div>
      )}

      {/* Step 5: Deploying */}
      {step === 5 && deploying && (
        <div style={{ padding: '32px', background: 'var(--card-bg)', borderRadius: '12px', textAlign: 'center' }}>
          <Loader size={48} style={{ animation: 'spin 1s linear infinite', margin: '0 auto 24px', color: '#ff9900' }} />
          <h2 style={{ fontSize: '24px', marginBottom: '16px' }}>Deploying Resources...</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>{deploymentMessage}</p>

          <div style={{ width: '100%', height: '8px', background: 'var(--body-bg)', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              width: `${deploymentProgress}%`,
              height: '100%',
              background: '#ff9900',
              transition: 'width 0.3s'
            }} />
          </div>
          <div style={{ marginTop: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
            {deploymentProgress}% complete
          </div>
        </div>
      )}

      {/* Step 5: Complete */}
      {step === 5 && !deploying && (
        <div style={{ padding: '32px', background: 'var(--card-bg)', borderRadius: '12px', textAlign: 'center' }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'rgba(16, 185, 129, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <Check size={48} style={{ color: '#10b981' }} />
          </div>

          <h2 style={{ fontSize: '32px', marginBottom: '16px' }}>Deployment Complete!</h2>
          <p style={{ fontSize: '18px', color: 'var(--text-secondary)', marginBottom: '32px' }}>
            Your AI infrastructure is now running on AWS
          </p>

          <button
            onClick={() => navigate('/aicloud/dashboard')}
            style={{
              padding: '16px 48px',
              background: '#ff9900',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Go to Dashboard
          </button>
        </div>
      )}
    </div>
  )
}
